import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

/**
 * Azure Application Insights — frontend telemetry (Phase 1d, TD-008).
 *
 * Replaces Sentry in PRODUCTION for PIPEDA compliance:
 *  - Data resides in the Canada Central App Insights resource (Canadian region).
 *  - No cookies (`disableCookiesUsage`).
 *  - Every telemetry item is run through a PHI scrubber (`addTelemetryInitializer`)
 *    before it leaves the browser — patient identifiers must never reach telemetry.
 *  - Only an opaque user id is ever attached (never email/name).
 *
 * Gated by env so the app runs identically with telemetry off:
 *   REACT_APP_APPINSIGHTS_ENABLED=true
 *   REACT_APP_APPINSIGHTS_CONNECTION_STRING=<from the Canada Central resource>
 *
 * The React plugin (@microsoft/applicationinsights-react-js) is intentionally NOT
 * used: its current major requires React 19 and we're on React 18. Route tracking
 * comes from `enableAutoRouteTracking` (browser History API, which React Router
 * drives); React render errors are reported via the app's ErrorBoundary
 * (see components/common/ErrorBoundary.tsx → trackAppError).
 */

const ENABLED = process.env.REACT_APP_APPINSIGHTS_ENABLED === 'true';
const CONNECTION_STRING = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '';

// --- PHI / PII scrubbing ---------------------------------------------------
// Deliberately broad — over-redaction is the safe direction for health data.
const REDACTED = '[redacted]';
const EMAIL = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const SIN = /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{3}\b/g;                       // Canadian SIN (9 digits)
const PHONE = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g; // NANP phone (10 digits)
const LONG_DIGITS = /\b\d{7,}\b/g;                                      // MRNs, ids, card-like runs

/**
 * Redact PHI/PII from a string. Exported pure for unit testing.
 * Order matters: emails first, then SIN (9) before phone (10) before generic runs.
 */
export function scrubPHI(text: string): string {
  return text
    .replace(EMAIL, REDACTED)
    .replace(SIN, REDACTED)
    .replace(PHONE, REDACTED)
    .replace(LONG_DIGITS, REDACTED);
}

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubPHI(value);
  if (value && typeof value === 'object') {
    scrubObject(value as Record<string, unknown>);
    return value;
  }
  return value;
}

function scrubObject(obj: Record<string, unknown> | undefined | null): void {
  if (!obj || typeof obj !== 'object') return;
  for (const key of Object.keys(obj)) {
    obj[key] = scrubValue(obj[key]);
  }
}

/**
 * Telemetry initializer: scrub every item before send. Exported for unit testing.
 * Returns true to send (scrubbed), false to drop (only if scrubbing itself throws —
 * we never let raw, unscrubbed data through).
 */
export function scrubTelemetryItem(item: ITelemetryItem): boolean {
  try {
    const base = item.baseData;
    if (base) {
      if (typeof base.message === 'string') base.message = scrubPHI(base.message);
      if (typeof base.uri === 'string') base.uri = scrubPHI(base.uri);
      if (typeof base.name === 'string') base.name = scrubPHI(base.name);
      if (Array.isArray(base.exceptions)) {
        for (const ex of base.exceptions) {
          if (ex && typeof ex.message === 'string') ex.message = scrubPHI(ex.message);
        }
      }
      scrubObject(base.properties);
    }
    scrubObject(item.data);
    return true;
  } catch {
    return false; // never emit an item we couldn't fully scrub
  }
}

// --- SDK lifecycle ---------------------------------------------------------
let _ai: ApplicationInsights | null = null;

export function initAppInsights(): ApplicationInsights | null {
  if (_ai || !ENABLED || !CONNECTION_STRING) return _ai;
  _ai = new ApplicationInsights({
    config: {
      connectionString: CONNECTION_STRING,
      disableCookiesUsage: true,        // PIPEDA: no cookies
      disablePageUnloadEvents: ['unload'], // 'unload' is blocked by Permissions-Policy in modern Chrome; pagehide still flushes
      enableAutoRouteTracking: true,    // SPA route changes → page views (React Router)
      autoTrackPageVisitTime: true,
    },
  });
  _ai.loadAppInsights();
  _ai.addTelemetryInitializer(scrubTelemetryItem); // scrub BEFORE first pageview
  _ai.trackPageView();
  return _ai;
}

export function getAppInsights(): ApplicationInsights | null {
  return _ai;
}

/** Attach only an opaque user id — never email/name. No cookie (PIPEDA). */
export function setAppInsightsUser(userId: string | number | null): void {
  if (!_ai) return;
  if (userId == null) {
    _ai.clearAuthenticatedUserContext();
    return;
  }
  _ai.setAuthenticatedUserContext(String(userId), undefined, false);
}

/** Report a caught error (e.g. from a React ErrorBoundary). No-op if disabled. */
export function trackAppError(error: Error, properties?: Record<string, unknown>): void {
  if (!_ai) return;
  const props = properties ? { ...properties } : undefined;
  if (props) scrubObject(props);
  _ai.trackException({ exception: error, properties: props as Record<string, string> | undefined });
}
